<!-- 三级/四级嵌套菜单组件 -->
<template>
  <div class="nested-menu-container" :class="{ 'is-collapsed': isCollapsed }">
    <ul class="nested-menu-list">
      <li
        v-for="(item, index) in menuList"
        :key="item.id || item.index || index"
        class="nested-menu-item"
        :class="{
          'is-active': isActive(item),
          'is-disabled': item.disabled,
          'has-children': hasChildren(item)
        }"
      >
        <!-- 菜单项内容 -->
        <div class="menu-item-content" @click="handleItemClick(item, $event)">
          <!-- 展开/折叠箭头 -->
          <i
            v-if="hasChildren(item) && !isCollapsed"
            class="arrow-icon"
            :class="{ 'is-expanded': item.expanded }"
            @click.stop="toggleExpand(item)"
          ></i>
          <!-- 状态图标 -->
          <span v-if="getStatusIcon(item)" class="status-icon">
            <i :class="getStatusIcon(item)"></i>
          </span>
          <!-- 菜单文本 -->
          <span
            v-if="!isCollapsed"
            class="menu-text"
            :title="getMenuText(item)"
            >{{ getMenuText(item) }}</span
          >
          <!-- 收起状态下显示前两个字符 -->
          <span
            v-if="isCollapsed"
            class="menu-text-short"
            :title="getMenuText(item)"
            >{{ getMenuTextShort(item) }}</span
          >
        </div>

        <!-- 子菜单（递归渲染） -->
        <transition name="slide-fade">
          <nested-menu
            v-if="hasChildren(item) && item.expanded && !isCollapsed"
            :menu-list="item.children"
            :active-key="activeKey"
            :level="level + 1"
            :is-collapsed="isCollapsed"
            class="submenu-wrapper"
            @item-click="handleChildClick"
          />
        </transition>
      </li>
    </ul>
    <!-- 收起/展开按钮 -->
    <div v-if="level === 0" class="collapse-toggle" @click="toggleCollapse">
      <img
        :src="
          !isCollapsed
            ? require('@assets/img/icon-left-arrow.png')
            : require('@assets/img/icon-right-arrow.png')
        "
        alt="collapse"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'NestedMenu',
  props: {
    // 菜单数据列表
    menuList: {
      type: Array,
      required: true,
      default: () => []
    },
    // 当前激活的菜单项key（用于匹配激活状态）
    activeKey: {
      type: [String, Number],
      default: ''
    },
    // 当前菜单层级（用于控制样式）
    level: {
      type: Number,
      default: 0
    },
    // 是否收起（整个菜单的收起状态）
    isCollapsed: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    /**
     * 切换整个菜单的收起/展开状态
     */
    toggleCollapse() {
      this.$emit('toggle-collapse')
    },
    /**
     * 判断是否有子菜单
     */
    hasChildren(item) {
      return (
        item.children &&
        Array.isArray(item.children) &&
        item.children.length > 0
      )
    },

    /**
     * 判断菜单项是否激活
     */
    isActive(item) {
      if (!this.activeKey) return false
      return (
        item.id === this.activeKey ||
        item.index === this.activeKey ||
        item.key === this.activeKey
      )
    },

    /**
     * 获取菜单显示文本
     */
    getMenuText(item) {
      return item.label || item.name || item.title || item.columnName || ''
    },

    /**
     * 获取菜单显示文本的前两个字符（用于收起状态）
     */
    getMenuTextShort(item) {
      const text = this.getMenuText(item)
      return text ? text.substring(0, 2) : ''
    },

    /**
     * 获取状态图标
     */
    getStatusIcon(item) {
      if (item.disabled) return 'el-icon-circle-close'
      if (item.icon) return item.icon
      return ''
    },

    /**
     * 切换展开/折叠状态
     */
    toggleExpand(item) {
      if (item.disabled) return
      this.$set(item, 'expanded', !item.expanded)
    },

    /**
     * 处理菜单项点击
     */
    handleItemClick(item, event) {
      if (item.disabled) return

      // 如果有子菜单，点击菜单项时切换展开状态（类似主菜单行为）
      if (this.hasChildren(item)) {
        this.toggleExpand(item)
      }

      // 触发点击事件
      this.$emit('item-click', item, event)
    },

    /**
     * 处理子菜单点击事件（向上传递）
     */
    handleChildClick(item, event) {
      this.$emit('item-click', item, event)
    }
  }
}
</script>

<style lang="scss" scoped>
.nested-menu-container {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  text-align: center;

  &.is-collapsed {
    .nested-menu-list {
      .menu-item-content {
        justify-content: center;
        padding: 10px 5px;
        flex-direction: column;
        align-items: center;
      }

      .menu-text {
        display: none;
      }

      .menu-text-short {
        display: block;
        font-size: 12px;
        color: #303133;
        text-align: center;
        margin-top: 4px;
        line-height: 1.2;
        word-break: keep-all;
        white-space: nowrap;
      }

      .arrow-icon {
        display: none;
      }

      .status-icon {
        margin-right: 0;
        margin-bottom: 4px;
      }
    }
  }
}

.nested-menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
}

.nested-menu-item {
  position: relative;
  user-select: none;

  &.has-children {
    .menu-item-content {
      cursor: pointer;
    }
  }

  &.is-active {
    .menu-item-content {
      color: #4054af;
      background-color: #d8ddf6;

      .menu-text {
        color: #4054af;
      }

      .arrow-icon {
        border-left-color: #606266;
      }

      .status-icon {
        color: #409eff;
      }
    }
  }

  &.is-disabled {
    .menu-item-content {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .status-icon {
      color: #c0c4cc;
    }
  }
}

.menu-item-content {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(.is-disabled) {
    background-color: #d8ddf6;
  }

  .arrow-icon {
    width: 0;
    height: 0;
    border-left: 5px solid #909399;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
    margin-right: 8px;
    transition: transform 0.2s;
    flex-shrink: 0;

    &.is-expanded {
      transform: rotate(90deg);
    }
  }

  .status-icon {
    margin-right: 8px;
    color: #409eff;
    flex-shrink: 0;
    font-size: 14px;
  }

  .menu-text {
    flex: 1;
    font-size: 14px;
    color: #303133;
  }
}

.submenu-wrapper {
  padding-left: 20px;
  background-color: #fafafa;
}

.collapse-toggle {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  img {
    width: 32px;
    height: 32px;
  }
  &::after {
    content: '';
    display: block;
    height: 1px;
    width: 100%;
    line-height: 50px;
    background-color: #f0f2f5;
    position: absolute;
    z-index: -1;
  }
}

.nested-menu-container.is-collapsed {
  .collapse-toggle {
    i {
      transform: rotate(180deg);
    }
  }
}

// 折叠/展开动画
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.slide-fade-enter,
.slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
